import { useState, useEffect } from "react";
import { Star, StarFour } from "@phosphor-icons/react";
import { toast } from "sonner";
import FollowPromptModal from "./FollowPromptModal";
import LoginPromptModal from "./LoginPromptModal";
import {
  isFollowing as libIsFollowing,
  followTruck,
  unfollowTruck,
  promptStateAfterFollow,
  markEmailPrompted,
  getFollowedIds,
} from "../lib/follows";
import {
  enablePushForTruck,
  isPushSupported,
  permissionState,
  isIosLike,
  isStandalone,
} from "../lib/push";
import { trackSiteEvent } from "../lib/analytics";
import { useAuth } from "../lib/auth";
import { maybeRequestAppReview } from "../lib/appRating";

/**
 * Tiny toggle pill for the map bottom sheet and list cards. Handles the
 * follow action optimistically and triggers the smart prompt modal when
 * appropriate (see `promptStateAfterFollow` logic in lib/follows).
 *
 * Hybrid login nudge (per product brief): on the **3rd** follow, if the
 * user is logged out AND the nudge hasn't fired this session, show the
 * `LoginPromptModal` with copy framed around saving favorites across
 * devices. The follow itself is NOT gated — anon users keep following
 * with localStorage; the modal is purely an upsell.
 */
const SIGNIN_NUDGE_SHOWN_KEY = "tt:follow:signin-nudge-shown";
const SIGNIN_NUDGE_THRESHOLD = 3;

// Fire ONE calendar-sync toast across the customer's lifetime — usually
// on the first follow. Stored in localStorage (not sessionStorage) so
// it never re-shows even across browser sessions.
const CALENDAR_PROMPT_KEY = "tt:follow:calendar-prompt-shown";

function _calendarPromptAlreadyShown() {
  try {
    return localStorage.getItem(CALENDAR_PROMPT_KEY) === "1";
  } catch {
    return true;
  }
}
function _markCalendarPromptShown() {
  try { localStorage.setItem(CALENDAR_PROMPT_KEY, "1"); } catch { /* ignore */ }
}

function _signinNudgeAlreadyShown() {
  try {
    return sessionStorage.getItem(SIGNIN_NUDGE_SHOWN_KEY) === "1";
  } catch {
    return true;
  }
}
function _markSigninNudgeShown() {
  try { sessionStorage.setItem(SIGNIN_NUDGE_SHOWN_KEY, "1"); } catch {}
}

/**
 * After a follow completes, surface a contextual one-tap "Notify me when
 * live?" CTA in a sonner toast — but only when push is in a state where
 * the click would actually result in a subscription:
 *   - browser supports push
 *   - permission is "default" (not asked yet) OR already "granted"
 *   - on iOS Safari, only when running as PWA (otherwise the toast would
 *     send the user down a dead-end path)
 *
 * The toast doubles as the success confirmation — no separate "Following"
 * toast in this branch, to avoid stacking.
 */
function maybeOfferNotifyToast(truckId, truckName, onSubscribed) {
  if (!isPushSupported()) return false;
  if (isIosLike() && !isStandalone()) return false;
  const perm = permissionState();
  if (perm === "denied") return false;

  trackSiteEvent("notif_follow_prompt_shown");
  // Snooze the generic soft prompt for the rest of the session — we just
  // gave the user a strictly-better, contextual ask. Don't double-prompt.
  try {
    sessionStorage.setItem("tt_push_prompt_shown_session", "1");
    window.dispatchEvent(new CustomEvent("tt:contextual-notify-shown"));
  } catch { /* ignore */ }

  // Sonner's toast() doesn't forward arbitrary attrs to the rendered li,
  // but it does expose a stable `data-id` attribute when an `id` is set.
  // Use that as the testid hook AND a custom JSX description with the
  // explicit data-testid so regression tooling can target either path.
  toast(
    (
      <div data-testid="notif-follow-toast">
        <div className="font-semibold">Following {truckName || "this truck"}</div>
        <div className="text-xs text-muted-foreground mt-0.5">
          Want a ping the moment they're live nearby?
        </div>
      </div>
    ),
    {
      id: "notif-follow-toast",
      duration: 9000,
      action: {
        label: "Notify me",
        onClick: async () => {
          trackSiteEvent("notif_follow_prompt_accepted");
          try {
            const res = await enablePushForTruck(truckId);
            if (res.ok) {
              onSubscribed?.();
              toast.success("You'll get a ping when this truck is live");
            } else if (res.reason === "denied") {
              toast.error("Notifications blocked. Re-enable in browser settings.");
            } else if (res.reason !== "ios_needs_pwa") {
              toast.error("Couldn't enable notifications. Try again.");
            }
          } catch {
            toast.error("Couldn't enable notifications. Try again.");
          }
        },
      },
      onAutoClose: () => trackSiteEvent("notif_follow_prompt_dismissed"),
      onDismiss: () => trackSiteEvent("notif_follow_prompt_dismissed"),
    }
  );
  return true;
}

export default function FollowButton({ truckId, truckName, compact = false, onChange }) {
  const { user } = useAuth();
  const [following, setFollowing] = useState(() => libIsFollowing(truckId));
  const [prompt, setPrompt] = useState(null); // "email" | "optin" | null
  const [signinOpen, setSigninOpen] = useState(false);

  // Keep local state in sync if the user follows/unfollows the same truck
  // from another surface (e.g. list + map sheet on the same page).
  useEffect(() => {
    setFollowing(libIsFollowing(truckId));
  }, [truckId]);

  const toggle = async (e) => {
    e?.stopPropagation?.();
    if (following) {
      setFollowing(false);
      await unfollowTruck(truckId);
      onChange?.(false);
      return;
    }
    // Optimistic — flip UI first.
    setFollowing(true);
    await followTruck(truckId);
    onChange?.(true);
    // Native app only, once per device: their first follow is the happy
    // moment — ask for an App Store rating (OS throttles the dialog).
    maybeRequestAppReview();

    const followCountAfter = getFollowedIds().length;

    // 3rd-follow signin nudge. Only fires for anonymous users (we don't
    // pester someone who already has an account) and only once per tab
    // session. Takes precedence over the email-only prompt because
    // signing up is the bigger value-prop and includes email collection.
    if (
      !user &&
      followCountAfter >= SIGNIN_NUDGE_THRESHOLD &&
      !_signinNudgeAlreadyShown()
    ) {
      _markSigninNudgeShown();
      setSigninOpen(true);
      return;
    }

    const state = promptStateAfterFollow();
    if (state === "email") {
      markEmailPrompted();
      setPrompt("email");
    } else if (state === "optin") {
      setPrompt("optin");
    } else {
      // Try the contextual "Notify me when live?" toast first; if push
      // isn't a viable path (denied / iOS Safari / unsupported) fall
      // back to the plain confirmation toast. Either way, the follow is
      // already saved.
      const offered = maybeOfferNotifyToast(truckId, truckName, () => {
        // Bell state lives in MapView; broadcast a lightweight event so
        // any mounted bell for this truck flips on without a refetch.
        try {
          window.dispatchEvent(new CustomEvent("tt:notify-subscribed", { detail: { truckId } }));
        } catch { /* ignore */ }
      });
      if (!offered) {
        toast.success(`Following ${truckName || "this truck"}`);
      }
      // One-time calendar-sync prompt — fires on the first follow (or
      // first follow after the storage was wiped). Survives across
      // sessions via localStorage so we don't pester repeat customers.
      if (!_calendarPromptAlreadyShown()) {
        _markCalendarPromptShown();
        // Slight delay so the success toast appears first and this
        // doesn't stack on top of it.
        setTimeout(() => {
          toast.message("Want these events on your calendar?", {
            description: "Subscribe once and every truck you follow auto-syncs to Apple, Google, or Outlook.",
            duration: 8000,
            action: {
              label: "Set up",
              onClick: () => {
                try {
                  trackSiteEvent("calendar_sync_prompt_clicked");
                } catch { /* ignore */ }
                window.location.assign("/calendar-sync");
              },
            },
          });
          try { trackSiteEvent("calendar_sync_prompt_shown"); } catch { /* ignore */ }
        }, 800);
      }
    }
  };

  const base =
    "inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors select-none";
  const sizing = compact ? "px-2.5 py-1 text-[11px]" : "px-3 py-1.5 text-[13px]";
  const onCls = "bg-amber-400 text-black hover:bg-amber-300 border border-amber-500/40";
  const offCls =
    "bg-background border border-border text-foreground hover:border-amber-400 hover:text-amber-600";

  return (
    <>
      <button
        type="button"
        onClick={toggle}
        className={`${base} ${sizing} ${following ? onCls : offCls}`}
        data-testid={following ? "follow-btn-active" : "follow-btn"}
        aria-pressed={following}
      >
        {following ? (
          <>
            <StarFour size={compact ? 12 : 14} weight="fill" aria-hidden="true" />
            Following
          </>
        ) : (
          <>
            <Star size={compact ? 12 : 14} weight="regular" aria-hidden="true" />
            Follow
          </>
        )}
      </button>
      {prompt && (
        <FollowPromptModal
          mode={prompt}
          truckName={truckName}
          onClose={() => setPrompt(null)}
        />
      )}
      <LoginPromptModal
        open={signinOpen}
        onClose={() => setSigninOpen(false)}
        title="Save your favorites across devices"
        body={`You're following ${getFollowedIds().length} trucks. Sign in so they sync to all your devices and you can get alerts when they go live.`}
      />
    </>
  );
}
